import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, UserPlus } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const ROLES = ['organizer', 'speaker', 'attendee', 'vendor', 'trip_member'];

export default function ParticipantsTab({ event, isOrganizer }) {
  const [parts, setParts] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('attendee');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.id}/participants`);
      setParts(r.data || []);
    } catch (err) {
      console.error('Failed to load participants:', err);
    } finally {
      setLoading(false);
    }
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email || !email.includes('@')) return toast.error('Enter a valid email');
    try {
      await api.post(`/events/${event.id}/invite`, { email, role });
      setEmail('');
      toast.success('Invited');
      load();
    } catch (err) {
      console.error('Failed to invite:', err);
      toast.error(err?.response?.data?.detail || 'Failed to invite');
    }
  };

  const changeRole = async (p, newRole) => {
    try {
      await api.put(`/events/${event.id}/participants/${p.id}/role`, { role: newRole });
      load();
    } catch (err) {
      console.error('Failed to change role:', err);
      toast.error('Failed to change role');
    }
  };
  const remove = async (p) => {
    try {
      await api.delete(`/events/${event.id}/participants/${p.id}`);
      load();
    } catch (err) {
      console.error('Failed to remove participant:', err);
      toast.error('Failed to remove participant');
    }
  };

  return (
    <div className="space-y-3" data-testid="participants-tab">
      {isOrganizer && (
        <Card className="rounded-2xl p-3">
          <div className="flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@domain.com" data-testid="invite-input" className="flex-1" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="px-3 rounded-xl border border-input bg-card text-sm h-10" data-testid="invite-role">
              {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
            </select>
            <Button onClick={invite} data-testid="invite-button" className="rounded-xl gradient-fab text-white"><UserPlus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Invite</span></Button>
          </div>
        </Card>
      )}
      {loading ? <div className="h-32 rounded-2xl bg-secondary animate-pulse" /> : (
        <div className="space-y-2" data-testid="participants-list">
          {parts.map((p) => (
            <Card key={p.id} className="p-3 rounded-2xl flex items-center gap-3" data-testid={`participant-${p.id}`}>
              <Avatar className="h-10 w-10 ring-1 ring-border">
                <AvatarImage src={p.user?.picture} />
                <AvatarFallback>{(p.user?.name || p.invited_email || 'M').slice(0,1).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{p.user?.name || p.invited_email}</div>
                <div className="text-xs text-muted-foreground truncate">{p.user?.email || p.invited_email}</div>
                <div className="flex gap-2 mt-1 flex-wrap">
                  <Badge variant="outline" className="text-[10px] rounded-full uppercase">{p.rsvp_status}</Badge>
                </div>
              </div>
              {isOrganizer ? (
                <div className="flex items-center gap-1">
                  <select value={p.role} onChange={(e) => changeRole(p, e.target.value)} className="px-2 h-9 rounded-xl border border-input bg-card text-xs" data-testid={`role-select-${p.id}`}>
                    {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                  </select>
                  <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`remove-participant-${p.id}`} className="rounded-xl text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ) : (
                <Badge className="text-[10px] rounded-full uppercase">{(p.role || '').replace('_',' ')}</Badge>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
